import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import { TerroristApis } from 'apis/TerroristApis';
import { PlantApi } from 'apis/PlantApis';
import { IPlant } from 'interfaces/Plant/Plant';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';

const DEFAULT_PAGE_SIZE = 10;

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────
const terrorismTypeLabel = (val: boolean | null) => {
  if (val === true) return <Tag color="success">An Toàn</Tag>;
  if (val === false) return <Tag color="error">Không An Toàn</Tag>;
  return <Tag color="default">Chưa xác định</Tag>;
};

const managerStatusLabel = (record: any) => {
  if (record.terrorismManagerApproval) {
    return <span className="text-[#1677ff] font-medium">Đã phê duyệt</span>;
  }
  return <span className="text-orange-500 font-medium">Chưa phê duyệt</span>;
};

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────
const TerroristVerification = () => {
  const { setTitle, setBreadcrumb } = useTitle();
  const { user } = useUser();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Modal xem chi tiết
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [matchedRecords, setMatchedRecords] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [editStatus, setEditStatus] = useState<boolean | null>(null);

  const [plants, setPlants] = useState<IPlant[]>([]);

  const { mutate: getAllPlant, isPending: isGetPlantsPending } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (res: any) => setPlants(res.data?.getPlant?.items || []),
    onError: (error) => console.log(error),
  });

  useEffect(() => {
    getAllPlant();
  }, []);

  useEffect(() => {
    if (detailVisible && selectedRecord) {
      setEditStatus(selectedRecord.terrorismType);
      fetchMatches();
    }
  }, [detailVisible, selectedRecord]);

  const fetchMatches = async () => {
    if (!selectedRecord) return;
    setLoadingMatches(true);
    try {
      const name =
        selectedRecord.name?.trim() ||
        selectedRecord.storeName?.trim() ||
        selectedRecord.firstName?.trim() ||
        '';
      if (!name) {
        setMatchedRecords([]);
        return;
      }
      const resTerror: any = await TerroristApis.getRecords({
        page: 1,
        pageSize: 100,
        fullName: name,
      });
      const resFatf: any = await TerroristApis.getFaft({
        page: 1,
        pageSize: 100,
        firstName: name,
      });

      const terrorItems =
        resTerror?.data?.getCounterTerrorismRecords?.items || [];
      const fatfItems = resFatf?.data?.getCounterTerrorismFaft?.items || [];

      const merged: any[] = [];
      terrorItems.forEach((r: any) => {
        merged.push({
          orgName: r.organizationName || '-',
          fullName: r.fullName || '-',
          nickName: r.nickname || '-',
          bod: r.bod || '-',
          gender: r.gender || '-',
          nationality: r.nationality || '-',
          hometown: r.hometown || '-',
          address: r.address || '-',
        });
      });
      fatfItems.forEach((r: any) => {
        merged.push({
          orgName: r.type || '-',
          fullName:
            [r.firstName, r.secondName, r.thirdName]
              .filter(Boolean)
              .join(' ') || '-',
          nickName: r.aliasName || '-',
          bod: '-',
          gender: '-',
          nationality: r.nationalities || '-',
          hometown: '-',
          address: r.address || '-',
        });
      });
      setMatchedRecords(merged);
    } catch (e) {
      console.error(e);
      message.error('Lỗi tải danh sách khớp');
    } finally {
      setLoadingMatches(false);
    }
  };

  useEffect(() => {
    setTitle('Xác minh khủng bố');
    setBreadcrumb([
      { title: 'Phòng chống khủng bố' },
      { title: 'Xác minh khủng bố' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const fetchData = useCallback(
    async (values?: any, currentPage = 1, currentPageSize = pageSize) => {
      setLoading(true);
      try {
        const params: any = {
          page: currentPage,
          pageSize: currentPageSize,
        };

        if (values?.plantId) params.plantId = values.plantId;

        // Trạng thái người gửi (terrorism_type)
        if (values?.terrorismType === 'safe') params.terrorismType = true;
        else if (values?.terrorismType === 'unsafe')
          params.terrorismType = false;

        // Trạng thái phê duyệt
        if (values?.managerStatus === 'approved')
          params.hasManagerApproval = true;
        else if (values?.managerStatus === 'pending')
          params.hasManagerApproval = false;

        if (values?.userDateFrom)
          params.userDateFrom = values.userDateFrom.toISOString();
        if (values?.userDateTo)
          params.userDateTo = values.userDateTo.toISOString();

        const res: any = await TerroristApis.getVerificationList(params);
        const result = res?.data?.getCustomer;
        setDataSource(result?.items ?? []);
        setTotal(result?.pageInfo?.totalItems ?? 0);
      } catch (err) {
        console.error(err);
        message.error('Lỗi khi tải danh sách');
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (values: any) => {
    setPage(1);
    fetchData(values, 1, pageSize);
  };

  const handleCancel = () => {
    form.resetFields();
    setPage(1);
    fetchData({}, 1, pageSize);
  };

  const handleApprove = async () => {
    if (!selectedRecord) return;
    setApproveLoading(true);
    try {
      await TerroristApis.updateCustomerTerrorism({
        id: selectedRecord.id,
        terrorismType: editStatus,
        terrorismManagerApproval: user?.id,
        terrorismManagerDate: new Date().toISOString(),
      });
      message.success('Phê duyệt thành công!');
      setDetailVisible(false);
      fetchData(form.getFieldsValue(), page, pageSize);
    } catch {
      message.error('Phê duyệt thất bại!');
    } finally {
      setApproveLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────
  // Columns
  // ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, index: number) =>
        (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Mã khách hàng',
      dataIndex: 'id',
      key: 'id',
      width: 140,
    },
    {
      title: 'Mã SAP',
      dataIndex: 'sapCustomerId',
      key: 'sapCustomerId',
      width: 120,
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'storeName',
      key: 'storeName',
      width: 200,
    },
    {
      title: 'Chi Nhánh',
      key: 'plantId',
      width: 100,
      render: (_: any, record: any) => record.terrorismUserPlant || '—',
    },
    {
      title: 'Trạng thái người gửi',
      key: 'terrorismType',
      width: 160,
      render: (_: any, record: any) => terrorismTypeLabel(record.terrorismType),
    },
    {
      title: 'Người gửi',
      key: 'sender',
      width: 160,
      render: (_: any, record: any) =>
        record.tterrorismUserApproval?.fullName ||
        record.terrorismUserApproval ||
        '—',
    },
    {
      title: 'Ngày gửi',
      key: 'terrorismUserDate',
      width: 140,
      render: (_: any, record: any) =>
        record.terrorismUserDate
          ? dayjs(record.terrorismUserDate).format('DD/MM/YYYY')
          : '—',
    },
    {
      title: 'Trạng thái người duyệt',
      key: 'managerStatus',
      width: 170,
      render: (_: any, record: any) => managerStatusLabel(record),
    },
    {
      title: 'Người duyệt',
      key: 'manager',
      width: 160,
      render: (_: any, record: any) =>
        record.tterrorismManagerApproval?.fullName ||
        record.terrorismManagerApproval ||
        '—',
    },
    {
      title: 'Ngày duyệt',
      key: 'terrorismManagerDate',
      width: 140,
      render: (_: any, record: any) =>
        record.terrorismManagerDate
          ? dayjs(record.terrorismManagerDate).format('DD/MM/YYYY')
          : '—',
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Tooltip title="Xem chi tiết">
          <Button
            type="link"
            icon={<EyeOutlined style={{ color: '#1677ff', fontSize: 18 }} />}
            onClick={() => {
              setSelectedRecord(record);
              setDetailVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* Filter Section */}
      <Card bordered={false} className="shadow-sm rounded-lg">
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12} md={5}>
              <Form.Item name="plantId" label="Chi nhánh" className="mb-0">
                <Select
                  placeholder="Chọn chi nhánh"
                  allowClear
                  showSearch
                  loading={isGetPlantsPending}
                  disabled={user?.isAccountant || user?.isTreasurer}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={plants.map((plant) => ({
                    label: plant?.name
                      ? `${plant.id} - ${plant.name}`
                      : plant?.id,
                    value: plant?.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item
                name="terrorismType"
                label="Trạng thái"
                className="mb-0"
              >
                <Select placeholder="Chọn trạng thái" allowClear>
                  <Select.Option value="safe">An toàn</Select.Option>
                  <Select.Option value="unsafe">Không an toàn</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item
                name="managerStatus"
                label="Trạng thái quản lý"
                className="mb-0"
              >
                <Select placeholder="Chọn trạng thái" allowClear>
                  <Select.Option value="approved">Đã phê duyệt</Select.Option>
                  <Select.Option value="pending">Chưa phê duyệt</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                name="userDateFrom"
                label="Ngày cập nhật từ"
                className="mb-0"
              >
                <DatePicker format="DD/MM/YYYY" className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item
                name="userDateTo"
                label="Ngày cập nhật đến"
                className="mb-0"
              >
                <DatePicker format="DD/MM/YYYY" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" className="mt-4">
            <Space>
              <Button onClick={handleCancel}>Huỷ</Button>
              <Button type="primary" htmlType="submit" className="bg-[#003B73]">
                Tìm kiếm
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>

      {/* Table */}
      <Card
        bordered={false}
        className="shadow-sm rounded-lg"
        bodyStyle={{ padding: 0 }}
      >
        <div className="px-4 pt-4 pb-1">
          <span className="text-base font-semibold text-gray-700">
            Danh sách xác minh khủng bố
          </span>
          <Badge count={total} showZero className="ml-2" color="#003B73" />
        </div>
        <div className="p-4 overflow-x-auto">
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (t) => `Tổng ${t} bản ghi`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
                fetchData(form.getFieldsValue(), newPage, newPageSize);
              },
            }}
          />
        </div>
      </Card>

      {/* Modal chi tiết */}
      <Modal
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
        title="Khách hàng có trong danh sách cần kiểm tra"
        destroyOnClose
      >
        {selectedRecord && (
          <div className="flex flex-col gap-6">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <span className="text-gray-500 text-sm">Mã Khách Hàng</span>
                <div
                  className="border border-gray-300 px-3 py-1.5 rounded bg-gray-50 mt-1 w-full break-words h-auto"
                  style={{ minHeight: '32px' }}
                >
                  {selectedRecord.id}
                </div>
              </Col>
              <Col span={6}>
                <span className="text-gray-500 text-sm">Mã SAP</span>
                <div
                  className="border border-gray-300 px-3 py-1.5 rounded bg-gray-50 mt-1 w-full break-words h-auto"
                  style={{ minHeight: '32px' }}
                >
                  {selectedRecord.sapCustomerId || '—'}
                </div>
              </Col>
              <Col span={6}>
                <span className="text-gray-500 text-sm">Chi nhánh</span>
                <div
                  className="border border-gray-300 px-3 py-1.5 rounded bg-gray-50 mt-1 w-full break-words h-auto"
                  style={{ minHeight: '32px' }}
                >
                  {selectedRecord.terrorismUserPlant || '—'}
                </div>
              </Col>
              <Col span={6}>
                <span className="text-gray-500 text-sm">Trạng thái</span>
                <Select
                  value={editStatus}
                  onChange={setEditStatus}
                  className="w-full mt-1 h-8"
                  options={[
                    { label: 'An toàn', value: true },
                    { label: 'Không an toàn', value: false },
                  ]}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <span className="text-gray-500 text-sm">Tên khách hàng</span>
                <div
                  className="border border-gray-300 px-3 py-1.5 rounded bg-gray-50 mt-1 w-full break-words h-auto"
                  style={{ minHeight: '32px' }}
                >
                  {selectedRecord.storeName ||
                    selectedRecord.name ||
                    selectedRecord.firstName ||
                    '—'}
                </div>
              </Col>
              <Col span={16}>
                <span className="text-gray-500 text-sm">Địa chỉ</span>
                <div
                  className="border border-gray-300 px-3 py-1.5 rounded bg-gray-50 mt-1 w-full break-words h-auto"
                  style={{ minHeight: '32px' }}
                >
                  {[
                    selectedRecord.address,
                    selectedRecord.tward?.name,
                    selectedRecord.tdistrict?.name,
                    selectedRecord.tcity?.name,
                  ]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </div>
              </Col>
            </Row>

            <Table
              loading={loadingMatches}
              dataSource={matchedRecords}
              pagination={false}
              size="small"
              bordered
              scroll={{ y: 300, x: 1000 }}
              columns={[
                { title: 'Tên tổ chức', dataIndex: 'orgName' },
                { title: 'Tên cá nhân', dataIndex: 'fullName' },
                { title: 'Nick Name / Tên khác', dataIndex: 'nickName' },
                { title: 'Ngày Sinh', dataIndex: 'bod' },
                { title: 'Giới tính', dataIndex: 'gender' },
                { title: 'Quốc tịch', dataIndex: 'nationality' },
                { title: 'Quê quán', dataIndex: 'hometown' },
                { title: 'Địa chỉ cư trú', dataIndex: 'address' },
              ]}
            />

            <div className="flex justify-end mt-2">
              <Button
                type="primary"
                loading={approveLoading}
                onClick={handleApprove}
                className="bg-green-600 hover:bg-green-700 px-8"
              >
                Lưu
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TerroristVerification;
